/**
 * DriverAccountDrawer — Rider-style slide-up sheet that consolidates
 * the entire account experience into a single overlay. No routes, no
 * separate pages.
 *
 * Sections (stacked, scrollable):
 *   1. Identity header
 *   2. Status segmented pill
 *   3. My Blocks (live counts, tap to drill in)
 *   4. Earnings summary
 *   5. Vehicles
 *   6. Documents
 *   7. Settings (theme · payouts placeholder · support · etc.)
 *   8. Sign Out
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  X, ChevronRight, Car, FileCheck, Settings as SettingsIcon, Plus, Star, Trash2,
  LogOut, Sun, Moon, Monitor, LifeBuoy, User, Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";
import { Sheet } from "@/shared/motion";

export default function DriverAccountDrawer({ open, onClose }) {
  const { logout } = useAuth();
  const [me, setMe] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/driver/me");
      setMe(data);
    } catch (ex) {
      toast.error(formatApiError(ex?.response?.data?.detail) || "Could not load account");
    }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Sheet
      open={open}
      onClose={onClose}
      className="bg-[#F9F7F4]"
      maxHeight="92vh"
      testid="driver-account-drawer"
      ariaLabel="Driver account"
    >
        {/* Drag handle */}
        <div className="flex items-center justify-center pt-3 pb-1">
          <span className="w-10 h-1 rounded-full bg-[#0A1128]/15" />
        </div>
        {/* Close pill */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-4 w-9 h-9 rounded-full bg-white border border-black/[0.06] shadow-sm inline-flex items-center justify-center text-[#0A1128] hover:bg-[#F1ECE3] link-pressable"
          aria-label="Close account"
          data-testid="driver-drawer-close"
        >
          <X size={16} strokeWidth={1.8} />
        </button>
        <div className="overflow-y-auto px-5 sm:px-6 pb-[max(env(safe-area-inset-bottom),32px)]" style={{ maxHeight: "calc(92vh - 24px)" }}>
          {/* Always render the drawer structure — fields gracefully
           *  display "—" while `me` loads so the entire sheet animates
           *  in as ONE cohesive surface (no "drawer slides up, content
           *  pops in" jank). */}
          <DrawerBody
            me={me || {}}
            loading={!me}
            onReload={load}
            onSignOut={() => { onClose?.(); setTimeout(() => logout(), 240); }}
          />
        </div>
    </Sheet>
  );
}

/* ====================================================================
 *                              BODY
 *
 * V5 — Account drawer is now identity + setup + preferences + legal
 * ONLY. All earnings, block history, and financial UI moved to the
 * Earnings Hub drawer (top-right $ button).
 * ==================================================================== */
function DrawerBody({ me, loading, onReload, onSignOut }) {
  const p = me.driver_profile || {};
  const initials = (me.first_name || me.name || "D").trim().charAt(0).toUpperCase();
  const greeting = greetingFor(new Date().getHours());

  return (
    <div className="space-y-5 pt-3">
      {/* Identity — Apple Wallet card-stack style. V6: status indicator
       *  removed; drivers operate on a block-based model (no online /
       *  offline / unavailable state). */}
      <header className="flex items-center gap-4 px-1" data-testid="drawer-header">
        <div className="w-16 h-16 rounded-full bg-[#0A1128] text-[#F9F7F4] inline-flex items-center justify-center font-serif text-2xl shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[9.5px] tracking-[0.32em] uppercase font-semibold text-[#ed6500]">Link Driver</div>
          <h1 className="font-serif font-light text-[24px] sm:text-[26px] leading-tight text-[#0A1128] truncate mt-0.5" data-testid="drawer-greeting">
            {greeting}, {me.first_name || "Driver"}
          </h1>
          {me.email && (
            <div className="text-[12px] text-[#515E7B] mt-0.5 truncate" data-testid="drawer-email">{me.email}</div>
          )}
        </div>
      </header>

      {/* SECTION: Profile */}
      <DrawerSection title="Profile">
        <ProfileCard me={me} />
      </DrawerSection>

      {/* SECTION: Driver setup */}
      <DrawerSection title="Driver setup">
        <VehiclesCard profile={p} onReload={onReload} />
        <DocumentsCard profile={p} onReload={onReload} />
      </DrawerSection>

      {/* SECTION: Preferences */}
      <DrawerSection title="Preferences">
        <SettingsCard profile={p} onReload={onReload} />
      </DrawerSection>

      {/* SECTION: Legal */}
      <DrawerSection title="Legal">
        <LegalCard />
      </DrawerSection>

      {/* Sign out */}
      <button
        onClick={onSignOut}
        className="mx-auto block mt-3 mb-6 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500] transition-colors py-3 px-4"
        data-testid="drawer-signout"
      >
        <LogOut size={12} /> Sign out
      </button>
    </div>
  );
}

function ProfileCard({ me }) {
  return (
    <Card title="Profile" testId="drawer-profile" icon={<User size={14} strokeWidth={1.8} className="text-[#ed6500]" />}>
      <ProfileRow label="Name"  value={`${me.first_name || ""} ${me.last_name || ""}`.trim() || me.name || "—"} />
      <ProfileRow label="Email" value={me.email || "—"} />
      <ProfileRow label="Phone" value={me.phone || "—"} />
    </Card>
  );
}
function ProfileRow({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-t border-black/[0.06] first:border-0">
      <span className="text-[11px] tracking-[0.22em] uppercase text-[#515E7B]">{label}</span>
      <span className="text-[13px] text-[#0A1128] font-medium truncate ml-3">{value}</span>
    </div>
  );
}

function LegalCard() {
  return (
    <Card title="Legal & Support" testId="drawer-legal" icon={<LifeBuoy size={14} strokeWidth={1.8} className="text-[#ed6500]" />}>
      <LegalRow label="Support" />
      <LegalRow label="Terms" />
      <LegalRow label="Privacy" />
    </Card>
  );
}
function LegalRow({ label }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-t border-black/[0.06] first:border-0">
      <span className="text-[13px] text-[#0A1128]">{label}</span>
      <ChevronRight size={14} className="text-[#0A1128]/40" />
    </div>
  );
}

function DrawerSection({ title, children }) {
  return (
    <div className="space-y-3">
      <div className="px-1 text-[10px] tracking-[0.28em] uppercase font-semibold text-[#515E7B]/85">{title}</div>
      {children}
    </div>
  );
}

function greetingFor(h) { return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; }

/* ===============================  Card primitive  =============================== */
function Card({ title, icon, action, children, testId }) {
  return (
    <section className="rounded-3xl bg-white border border-black/[0.06] shadow-sm p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <h2 className="font-serif text-[16px] text-[#0A1128]">{title}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/* ===============================  Removed in V5  ===============================
 * MyBlocksCard + EarningsCard + EBox + Mini have been retired. All
 * earnings/block-history UI lives in the new DriverEarningsHubDrawer
 * (top-right $ button). */

/* ===============================  Vehicles  =============================== */
function VehiclesCard({ profile, onReload }) {
  const [adding, setAdding] = useState(false);
  const vehicles = profile.vehicles || [];
  return (
    <Card
      title="Vehicles"
      icon={<Car size={14} strokeWidth={1.8} className="text-[#ed6500]" />}
      testId="drawer-vehicles"
    >
      {vehicles.length === 0 && !adding && (
        <div className="text-[12px] text-[#515E7B] mb-3">No vehicles added yet.</div>
      )}
      <div className="space-y-1.5">
        {vehicles.map((v) => (
          <div key={v.id} className="flex items-center justify-between gap-2 rounded-xl bg-[#F9F7F4] border border-black/[0.05] px-3 py-2" data-testid={`drawer-vehicle-${v.id}`}>
            <div className="min-w-0">
              <div className="text-[13px] text-[#0A1128] truncate">{v.year} {v.make} {v.model}</div>
              <div className="text-[10.5px] text-[#515E7B] truncate">{v.plate} · {v.color}</div>
            </div>
            <div className="flex items-center gap-2">
              {v.is_primary ? (
                <span className="text-[9px] tracking-[0.18em] uppercase font-semibold px-2 py-0.5 rounded-full bg-[#ed6500]/10 text-[#ed6500] border border-[#ed6500]/30 inline-flex items-center gap-1">
                  <Star size={10} /> Primary
                </span>
              ) : (
                <button type="button" onClick={async () => { try { await api.post(`/driver/vehicles/${v.id}/primary`); onReload(); } catch (ex) { toast.error(formatApiError(ex?.response?.data?.detail) || "Failed"); } }}
                  className="text-[9px] tracking-[0.18em] uppercase font-semibold text-[#515E7B] hover:text-[#ed6500]"
                  data-testid={`drawer-vehicle-${v.id}-make-primary`}>Make primary</button>
              )}
              <button type="button" onClick={async () => { try { await api.delete(`/driver/vehicles/${v.id}`); onReload(); } catch (ex) { toast.error(formatApiError(ex?.response?.data?.detail) || "Failed"); } }} className="text-[#515E7B] hover:text-red-600" data-testid={`drawer-vehicle-${v.id}-remove`}>
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>
      {adding ? <VehicleForm onCancel={() => setAdding(false)} onSubmit={async (payload) => { try { await api.post("/driver/vehicles", payload); setAdding(false); onReload(); } catch (ex) { toast.error(formatApiError(ex?.response?.data?.detail) || "Failed"); } }} />
        : <button type="button" onClick={() => setAdding(true)} className="mt-3 w-full h-10 rounded-xl border border-dashed border-[#0A1128]/15 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0A1128] hover:border-[#ed6500] hover:text-[#ed6500] inline-flex items-center justify-center gap-1.5" data-testid="drawer-vehicle-add"><Plus size={12}/> Add vehicle</button>}
    </Card>
  );
}
function VehicleForm({ onCancel, onSubmit }) {
  const [form, setForm] = useState({ make: "", model: "", year: "", color: "", plate: "" });
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!form.make.trim() || !form.model.trim() || !form.year.trim()) { toast.error("Make, model and year are required"); return; }
    setSubmitting(true);
    try { await onSubmit({ ...form, year: parseInt(form.year, 10) || 0 }); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="mt-3 grid grid-cols-2 gap-2">
      <VehicleField k="make"  label="Make"  form={form} setForm={setForm}/>
      <VehicleField k="model" label="Model" form={form} setForm={setForm}/>
      <VehicleField k="year"  label="Year" type="number" form={form} setForm={setForm}/>
      <VehicleField k="color" label="Color" form={form} setForm={setForm}/>
      <div className="col-span-2"><VehicleField k="plate" label="License plate" form={form} setForm={setForm}/></div>
      <button type="button" onClick={onCancel} className="h-10 rounded-xl bg-[#F9F7F4] border border-black/[0.06] text-[11px] tracking-[0.22em] uppercase font-semibold text-[#0A1128]" data-testid="drawer-vehicle-cancel">Cancel</button>
      <button type="button" onClick={submit} disabled={submitting} className="h-10 rounded-xl bg-[#ed6500] text-white text-[11px] tracking-[0.22em] uppercase font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-1.5" data-testid="drawer-vehicle-submit">
        {submitting && <Loader2 size={12} className="animate-spin"/>} Save vehicle
      </button>
    </div>
  );
}

/* ===============================  Documents  =============================== */
function DocumentsCard({ profile, onReload }) {
  const byType = Object.fromEntries((profile.documents || []).map((d) => [d.type, d]));
  const upload = async (type) => {
    try { await api.post("/driver/documents", { type, file_url: `placeholder://${type}/${Date.now()}` }); onReload(); }
    catch (ex) { toast.error(formatApiError(ex?.response?.data?.detail) || "Upload failed"); }
  };
  const types = [
    { type: "license",  label: "Driver License" },
    { type: "insurance", label: "Proof of Insurance" },
    { type: "registration", label: "Vehicle Registration" },
    { type: "inspection", label: "Vehicle Inspection" },
    { type: "background", label: "Background Check" },
  ];
  return (
    <Card
      title="Documents"
      icon={<FileCheck size={14} strokeWidth={1.8} className="text-[#ed6500]" />}
      testId="drawer-documents"
    >
      <ul className="space-y-1.5">
        {types.map((t) => {
          const doc = byType[t.type];
          const status = doc?.status;
          return (
            <li key={t.type} className="flex items-center justify-between text-[13px] py-1.5">
              <span className="text-[#0A1128]">{t.label}</span>
              {status === "approved" ? <Pill tone="emerald" testId={`drawer-doc-${t.type}`}>Approved</Pill>
                : status === "pending" ? <Pill tone="amber" testId={`drawer-doc-${t.type}`}>Pending</Pill>
                : <button type="button" onClick={() => upload(t.type)} className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]" data-testid={`drawer-doc-${t.type}`}>Upload</button>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
function Pill({ tone, children, testId }) {
  const cls = tone === "emerald" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200";
  return <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold border ${cls}`} data-testid={testId}>{children}</span>;
}

/* ===============================  Settings  =============================== */
function SettingsCard({ profile, onReload }) {
  const themePref = profile.theme || "light";
  const setTheme = async (t) => {
    try { await api.put("/driver/theme", { theme: t }); onReload(); }
    catch (ex) { toast.error(formatApiError(ex?.response?.data?.detail) || "Failed"); }
  };
  const themeOptions = [
    { key: "light",  label: "Light",  Icon: Sun },
    { key: "dark",   label: "Dark",   Icon: Moon },
    { key: "system", label: "System", Icon: Monitor },
  ];
  return (
    <Card
      title="Settings"
      icon={<SettingsIcon size={14} strokeWidth={1.8} className="text-[#ed6500]" />}
      testId="drawer-settings"
    >
      <div className="py-1" data-testid="drawer-settings-theme">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] text-[#0A1128]">Appearance</span>
          <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]" data-testid="drawer-settings-theme-current">{themePref}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {themeOptions.map(({ key, label, Icon }) => {
            const active = themePref === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setTheme(key)}
                className={`h-10 rounded-xl text-[10px] tracking-[0.22em] uppercase font-semibold border inline-flex items-center justify-center gap-1.5 ${active ? "bg-[#ed6500] text-white border-[#ed6500]" : "bg-[#F9F7F4] text-[#0A1128] border-black/[0.08] hover:bg-black/[0.04]"}`}
                data-testid={`drawer-settings-theme-${key}`}
              >
                <Icon size={12}/> {label}
              </button>
            );
          })}
        </div>
      </div>
      <Row left="Notifications"            right={<Soon />} testId="drawer-settings-notifs" />
      <Row left="Location permissions"     right={<Soon />} testId="drawer-settings-loc" />
      <Row left="Navigation preferences"   right={<Soon />} testId="drawer-settings-nav" />
    </Card>
  );
}
function Row({ left, right, testId }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-t border-black/[0.06]" data-testid={testId}>
      <span className="text-[13px] text-[#0A1128]">{left}</span>
      <span>{right}</span>
    </div>
  );
}
function Soon() {
  return <span className="px-2 h-5 rounded-full text-[9px] tracking-[0.18em] uppercase inline-flex items-center bg-black/[0.06] text-[#515E7B]">Soon</span>;
}

function VehicleField({ k, label, type = "text", form, setForm }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.18em] uppercase text-[#515E7B]">{label}</span>
      <input
        type={type}
        value={form[k]}
        onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))}
        className="mt-1 w-full h-10 rounded-xl bg-[#F9F7F4] border border-black/[0.06] px-3 text-[13px] text-[#0A1128] focus:outline-none focus:border-[#ed6500]"
        data-testid={`drawer-vehicle-field-${k}`}
      />
    </label>
  );
}
